import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, DataTable, Modal, PageHeader, SearchFilterBar,
  formatDateTime, formatDateWithYear, type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { SUBJECT_NAMES } from '@padcanvas/shared';
import {
  billingApi, formatRupees, ORDER_STATUSES, PLAN_LABEL,
  type Order, type OrderStatus,
} from '../../api/billingApi';
import './Billing.css';

/**
 * Orders — every purchase, ₹0 ones included, and the record of what each
 * granted. A ₹0 order left `created` (its fulfilment failed part-way) can be
 * finished here; fulfilment is idempotent per line.
 *
 * Design: docs/FeatureDesigns/billing/subscriptions-and-payments-target.md §6, §11.
 */

const STATUS_VARIANT: Record<OrderStatus, 'success' | 'info' | 'default' | 'warning' | 'error'> = {
  fulfilled: 'success', paid: 'info', created: 'warning', failed: 'error', expired: 'default',
};

const userLink = (id: string) => (
  <Link className="bill-mono" to={`/users/${encodeURIComponent(id)}`} onClick={e => e.stopPropagation()}>{id}</Link>
);

const subjectsLabel = (subjects: string[]) =>
  (subjects.length ? subjects.map(s => SUBJECT_NAMES[s] || s).join(', ') : 'All');

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const filters: Record<string, string> = {
    status: params.get('status') ?? '',
    code: params.get('code') ?? '',
    userId: params.get('userId') ?? '',
  };
  const openId = params.get('order');
  const [sort, setSort] = useState<SortState>({ sortBy: 'created', sortOrder: 'desc' });

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['billingOrders', filters],
    queryFn: () => billingApi.listOrders(filters),
  });

  const setFilters = (next: Record<string, string>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) if (v?.trim()) p.set(k, v.trim());
    setParams(p);
  };

  const openOrder = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('order', id); else p.delete('order');
    setParams(p);
  };

  const filterDefs: FilterDef[] = [
    {
      key: 'status', kind: 'select', placeholder: 'All statuses',
      options: ORDER_STATUSES.map(s => ({ value: s, label: s })),
    },
    { key: 'code', kind: 'text', placeholder: 'Code' },
    { key: 'userId', kind: 'text', placeholder: 'Buyer userId' },
  ];

  const columns: ColumnDef<Order>[] = [
    {
      key: 'created', header: 'Created', sortable: true, sortValue: o => o.createdAt,
      render: o => formatDateTime(o.createdAt),
    },
    {
      key: 'id', header: 'Order', mobile: 'title',
      render: o => <span className="bill-mono">{o.id}</span>,
    },
    {
      key: 'buyer', header: 'Buyer',
      render: o => <>{userLink(o.userId)}<p className="bill-sub">{o.buyerKind}</p></>,
    },
    {
      key: 'plan', header: 'Plan', sortable: true, sortValue: o => o.plan,
      render: o => <>{PLAN_LABEL[o.plan] ?? o.plan}<p className="bill-sub">{o.length}</p></>,
    },
    { key: 'lines', header: 'Lines', className: 'bill-num', render: o => o.lines.length },
    {
      key: 'list', header: 'List', className: 'bill-num', sortable: true, sortValue: o => o.listTotalPaise,
      render: o => formatRupees(o.listTotalPaise),
    },
    {
      key: 'code', header: 'Code', sortable: true, sortValue: o => o.code ?? '',
      render: o => (o.code ? <><span className="bill-mono">{o.code}</span> <span className="bill-sub">−{o.discountPct ?? 0}%</span></> : '—'),
    },
    {
      key: 'total', header: 'Total', className: 'bill-num', sortable: true, sortValue: o => o.totalPaise,
      render: o => formatRupees(o.totalPaise),
    },
    {
      key: 'status', header: 'Status', sortable: true, mobile: 'accessory', sortValue: o => o.status,
      render: o => <Badge variant={STATUS_VARIANT[o.status] ?? 'default'} size="sm">{o.status}</Badge>,
    },
  ];

  return (
    <div className="bill">
      <PageHeader
        title="Orders"
        subtitle="Every purchase, ₹0 included. Prices are GST-inclusive. Open a row for its lines."
      />

      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        hideSearch
        loading={isFetching}
      />

      {error
        ? <p className="bill-error">Error: {(error as Error).message}</p>
        : (
          <DataTable<Order>
            columns={columns}
            data={data ?? []}
            rowKey={o => o.id}
            onRowClick={o => openOrder(o.id)}
            sort={sort}
            onSortChange={setSort}
            loading={isLoading}
            mobileCards
            emptyMessage="No orders match."
          />
        )}

      <Modal
        isOpen={!!openId}
        onClose={() => openOrder(null)}
        title={openId ? `Order ${openId}` : undefined}
        size="lg"
        closeOnOverlayClick
        closeOnEscape
      >
        {openId && <OrderDetail id={openId} initial={data?.find(o => o.id === openId)} />}
      </Modal>
    </div>
  );
}

function OrderDetail({ id, initial }: { id: string; initial?: Order }) {
  const queryClient = useQueryClient();
  const { data: order, isLoading, error } = useQuery({
    queryKey: ['billingOrder', id],
    queryFn: () => billingApi.getOrder(id),
    initialData: initial,
  });

  const fulfil = useMutation({
    mutationFn: () => billingApi.fulfilOrder(id),
    onSuccess: updated => {
      queryClient.setQueryData(['billingOrder', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['billingOrders'] });
    },
  });

  if (error) return <p className="bill-error">Error: {(error as Error).message}</p>;
  if (isLoading || !order) return <div className="loading">Loading...</div>;

  const lineColumns: ColumnDef<Order['lines'][number]>[] = [
    { key: 'student', header: 'Student', render: l => userLink(l.studentId) },
    { key: 'subjects', header: 'Subjects', render: l => subjectsLabel(l.subjects) },
    { key: 'start', header: 'Start', render: l => formatDateWithYear(l.startDate) },
    { key: 'end', header: 'End', render: l => formatDateWithYear(l.endDate) },
    { key: 'list', header: 'List', className: 'bill-num', render: l => formatRupees(l.listPaise) },
    { key: 'price', header: 'Price', className: 'bill-num', render: l => formatRupees(l.pricePaise) },
  ];

  const facts: [string, ReactNode][] = [
    ['Status', <Badge variant={STATUS_VARIANT[order.status] ?? 'default'} size="sm">{order.status}</Badge>],
    ['Buyer', <>{userLink(order.userId)} <span className="bill-sub">({order.buyerKind})</span></>],
    ['Plan', `${PLAN_LABEL[order.plan] ?? order.plan} · ${order.length}`],
    ['Tenant', order.tenantId],
    ['List total', formatRupees(order.listTotalPaise)],
    ['Code', order.code ? `${order.code} (−${order.discountPct ?? 0}%)` : '—'],
    ['Total', formatRupees(order.totalPaise)],
    ['GST in total', formatRupees(order.gstPaise)],
    ['Created', `${formatDateTime(order.createdAt)} by ${order.createdBy}`],
    ['Fulfilled', order.fulfilledAt ? formatDateTime(order.fulfilledAt) : '—'],
  ];

  const canFulfil = order.status === 'created' && order.totalPaise === 0;

  return (
    <>
      <dl className="bill-detail-grid">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <DataTable<Order['lines'][number]>
        columns={lineColumns}
        data={order.lines}
        rowKey={l => `${l.studentId}:${l.startDate}`}
        compact
        emptyMessage="This order has no lines."
      />

      {canFulfil && (
        <div className="bill-detail-actions">
          <Button variant="primary" size="sm" onClick={() => fulfil.mutate()} loading={fulfil.isPending}>
            Fulfil
          </Button>
          <span className="bill-sub">Grants each line's plan. Safe to repeat: a line already granted is skipped.</span>
          {fulfil.isError && <span className="status-error">{(fulfil.error as Error).message}</span>}
        </div>
      )}
    </>
  );
}
